import React, { useState, useEffect, useRef } from 'react';
import { Box, MobileStepper, useTheme, Button, Typography, keyframes, IconButton, Tooltip } from '@mui/material';
import { KeyboardArrowLeft, KeyboardArrowRight, KeyboardArrowDown, KeyboardDoubleArrowLeft, KeyboardDoubleArrowRight, Brightness4, Brightness7 } from '@mui/icons-material';
import { useColorMode } from 'src/theme/ThemeProvider';
import Problem from './Problem';
import Solutions from './Solutions';
import MlsRevenueProjection from './MlsRevenueProjection';
import { useAppDispatch } from 'src/store';
import { trackAnalytics } from 'src/features/analytics/analytics.slice';
import TeamSlide from './TeamSlide';
import TamSomSlide from './TamSomSlide';
import ProgressSlide from './ProgressSlide';
import ThreeDOppySlide from './ThreeDOppySlide';
import CustomerTractionSlide from './CustomerTractionSlide';
import MissionSlide from './MissionSlide';
import EmailCaptureModal from './EmailCaptureModal';

// Define bounce animations
const bounceDown = keyframes`
  0%, 20%, 50%, 80%, 100% {
    transform: translateY(0);
  }
  40% {
    transform: translateY(-10px);
  }
  60% {
    transform: translateY(-5px);
  }
`;

const bounceHorizontal = keyframes`
  0%, 20%, 50%, 80%, 100% {
    transform: translateX(0);
  }
  40% {
    transform: translateX(-10px);
  }
  60% {
    transform: translateX(-5px);
  }
`;

// Scroll indicator component
const ScrollIndicator: React.FC<{ direction: 'down' | 'left' | 'right'; visible: boolean }> = ({ direction, visible }) => {
  const theme = useTheme();
  
  const getIcon = () => {
    switch (direction) {
      case 'down':
        return <KeyboardArrowDown sx={{ fontSize: '2rem' }} />;
      case 'left':
        return <KeyboardDoubleArrowLeft sx={{ fontSize: '2rem' }} />;
      case 'right':
        return <KeyboardDoubleArrowRight sx={{ fontSize: '2rem' }} />;
    }
  };
  
  const getPosition = () => {
    switch (direction) {
      case 'down':
        return {
          bottom: 140,
          left: '50%',
          transform: 'translateX(-50%)',
          animation: `${bounceDown} 2s infinite`,
        };
      case 'left':
        return {
          top: '50%',
          left: 20,
          transform: 'translateY(-50%)',
          animation: `${bounceHorizontal} 2s infinite`,
        };
      case 'right':
        return {
          top: '50%',
          right: 20,
          transform: 'translateY(-50%)',
          animation: `${bounceHorizontal} 2s infinite`,
        };
    }
  };
  
  return (
    <Box
      sx={{
        position: 'fixed',
        zIndex: 999,
        display: visible ? 'flex' : 'none',
        flexDirection: 'column',
        alignItems: 'center',
        color: theme.palette.mode === 'dark' ? 'white' : 'black',
        pointerEvents: 'none',
        backgroundColor: theme.palette.mode === 'dark' 
          ? 'rgba(0, 0, 0, 0.8)' 
          : 'rgba(255, 255, 255, 0.9)',
        backdropFilter: 'blur(8px)',
        borderRadius: '24px',
        padding: '12px',
        boxShadow: theme.palette.mode === 'dark'
          ? '0 4px 20px rgba(0, 0, 0, 0.5)'
          : '0 4px 20px rgba(0, 0, 0, 0.15)',
        ...getPosition(),
      }}
    >
      {getIcon()}
      {direction === 'down' && (
        <Typography variant="caption" sx={{ mt: 0.5, fontWeight: 600 }}>
          Scroll {direction}
        </Typography>
      )}
    </Box>
  );
};

const Deck: React.FC = () => {
  const theme = useTheme();
  const { toggleColorMode } = useColorMode();
  const [currentSlide, setCurrentSlide] = useState(0);
  const [scrollIndicatorsVisible, setScrollIndicatorsVisible] = useState({
    down: true,
    left: true,
    right: true,
  });

  // State for email modal and user info
  const [emailModalOpen, setEmailModalOpen] = useState(true);
  const [userEmail, setUserEmail] = useState('');
  const [userName, setUserName] = useState('');

  const slides = [
    ThreeDOppySlide,
    MissionSlide,
    Problem,
    Solutions,
    CustomerTractionSlide,
    ProgressSlide,
    TamSomSlide,
    MlsRevenueProjection,
    TeamSlide,
  ];

  // Define which slides need scroll indicators
  const slidesWithScrolling = {
    2: 'down', // Problem
    3: 'down', // Solutions
    4: 'down', // CustomerTractionSlide
    5: 'horizontal', // ProgressSlide (Timeline)
    6: 'down', // TamSomSlide
    7: 'down', // MlsRevenueProjection
    8: 'down', // TeamSlide
  };

  const dispatch = useAppDispatch();

  // Handle scroll events to show/hide indicators
  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const element = e.currentTarget;
    const scrollType = slidesWithScrolling[currentSlide];
    
    
    if (scrollType === 'down') {
      const isAtBottom = element.scrollHeight - element.scrollTop - element.clientHeight < 50;
      setScrollIndicatorsVisible(prev => ({ ...prev, down: !isAtBottom }));
    } else if (scrollType === 'horizontal') {
      const isAtLeft = element.scrollLeft < 50;
      const isAtRight = element.scrollWidth - element.scrollLeft - element.clientWidth < 50;
      setScrollIndicatorsVisible(prev => ({ 
        ...prev, 
        left: !isAtLeft,
        right: !isAtRight 
      }));
    }
  };

  // Log current slide number whenever it changes
  useEffect(() => {
    console.log(`Current slide is now ${currentSlide + 1} of ${slides.length}`);
  }, [currentSlide]);


  // Handle arrow key navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      console.log('Key pressed:', e.key);

      if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
        e.preventDefault(); // Prevent default scrolling
        console.log('Default action prevented for right/down arrow');
        handleNext();
      } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
        e.preventDefault(); // Prevent default scrolling
        console.log('Default action prevented for left/up arrow');
        handleBack();
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    // Cleanup event listener on unmount
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  const handleNext = () => {
    setCurrentSlide((prev) => {
      const newSlide = Math.min(prev + 1, slides.length - 1);
      console.log(`Current slide: ${prev}, Next slide: ${newSlide}`);
      dispatch(
        trackAnalytics({
          action_name: 'next_slide',
          payload: { from: prev, to: newSlide },
        })
      );
      return newSlide;
    });
  };

  const handleBack = () => {
    setCurrentSlide((prev) => {
      const newSlide = Math.max(prev - 1, 0);
      console.log(`Current slide: ${prev}, Previous slide: ${newSlide}`);
      dispatch(
        trackAnalytics({
          action_name: 'previous_slide',
          payload: { from: prev, to: newSlide },
        })
      );
      return newSlide;
    });
  };
  
  // Handle clicking on dots to jump to slide
  const handleDotClick = (index: number) => {
    setCurrentSlide(index);
    console.log(`Jumped to slide: ${index + 1}`);
    dispatch(
      trackAnalytics({
        action_name: 'jump_to_slide',
        payload: { from: currentSlide, to: index },
      })
    );
  };

  // Handle email modal closure
  const handleEmailModalClose = (email: string, name: string) => {
    console.log('Email modal closed with email:', email, 'name:', name);
    setUserEmail(email); // Save email
    setUserName(name);   // Save name

    setEmailModalOpen(false); // Close modal

    // Dispatch analytics event with user's name and email
    dispatch(trackAnalytics({
      action_name: 'email_captured_on_pitch_deck',
      payload: { email, name },
    }));
  };

  // Trigger analytics event on deck visit
  useEffect(() => {
    console.log('User visited pitch deck');
    dispatch(trackAnalytics({
      action_name: 'pitch_deck_visited',
      payload: {}, // No payload needed
    }));
  }, []); // Empty dependency array to run only once on mount

  return (
    <>
      {/* Email Capture Modal */}
      <EmailCaptureModal open={emailModalOpen} onClose={handleEmailModalClose} />

      {/* Render slides */}
      {!emailModalOpen && (
        <Box
          sx={{
            width: '100%',
            height: '100vh',
            overflow: 'hidden',
            p: 0,
            backgroundColor: theme.palette.background.default,
            position: 'relative',
          }}
        >
          {/* Theme toggle button - floating in top right */}
          <Tooltip title={theme.palette.mode === 'dark' ? 'Light mode' : 'Dark mode'}>
            <IconButton
              onClick={toggleColorMode}
              sx={{
                position: 'fixed',
                top: 16,
                right: 16,
                zIndex: 1001,
                backgroundColor: theme.palette.background.glass,
                backdropFilter: 'blur(8px)',
                color: theme.palette.text.secondary,
                opacity: 0.6,
                '&:hover': {
                  opacity: 1,
                  backgroundColor: theme.palette.background.glassStrong,
                },
                transition: 'all 0.2s ease',
              }}
            >
              {theme.palette.mode === 'dark' ? <Brightness7 /> : <Brightness4 />}
            </IconButton>
          </Tooltip>

          {/* Render slides */}
          {slides.map((Slide, index) => (
            <Box
              key={index}
              onScroll={handleScroll}
              sx={{
                width: '100%',
                height: 'calc(100vh - 115px)', // Subtract navigation bar height
                overflowY: index === 5 ? 'hidden' : 'auto', // Timeline needs horizontal scroll
                overflowX: index === 5 ? 'auto' : 'hidden', // Timeline horizontal scroll
                transition: 'transform 0.5s ease-in-out',
                transform: `translateY(${(index - currentSlide) * 100}%)`,
                position: 'absolute',
                top: 0,
                left: 0,
                display: 'flex',
                flexDirection: 'column',
                '&::-webkit-scrollbar': {
                  width: '8px',
                  height: '8px',
                },
                '&::-webkit-scrollbar-track': {
                  backgroundColor: theme.palette.background.overlay,
                },
                '&::-webkit-scrollbar-thumb': {
                  backgroundColor: theme.palette.background.overlayMedium,
                  borderRadius: '4px',
                },
              }}
            >
              <Box sx={{ 
                display: 'flex',
                flexDirection: 'column',
              }}>
                <Slide />
              </Box>
            </Box>
          ))}
          
          {/* Show scroll indicator for current slide if needed */}
          {slidesWithScrolling[currentSlide] && (
            <>
              {slidesWithScrolling[currentSlide] === 'down' && (
                <ScrollIndicator direction="down" visible={scrollIndicatorsVisible.down} />
              )}
              {slidesWithScrolling[currentSlide] === 'horizontal' && (
                <>
                  <ScrollIndicator direction="left" visible={scrollIndicatorsVisible.left} />
                  <ScrollIndicator direction="right" visible={scrollIndicatorsVisible.right} />
                </>
              )}
            </>
          )}
          {/* Log height adjustment */}
          <Box
            sx={{
              position: 'fixed', // Fixed to bottom of the browser
              bottom: 0,
              left: 0,
              right: 0,
              backgroundColor: theme.palette.background.glassHeavy,
              backdropFilter: 'blur(12px)',
              borderTop: `1px solid ${theme.palette.background.overlayLight}`,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
              alignItems: 'baseline',
              width: '100%',
              height: '115px',
              gap: 0,
              padding: .5,
              zIndex: 1000,
              boxShadow: theme.palette.mode === 'dark'
                ? '0 -4px 20px rgba(0, 0, 0, 0.5)'
                : '0 -4px 20px rgba(0, 0, 0, 0.1)',
            }}
          >
            {/* First Row: Invest Button */}
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                width: '100%',
                mb: 1,
              }}
            >
              <Button
                href="https://oppy.pro?ref=deck"
                target="_blank"
                sx={{
                  color: theme.palette.text.secondary,
                  textTransform: 'none',
                  height:'100%'
                }}
                onClick={() => {
                  console.log('Oppy button clicked, opening Oppy website');
                }}
              >
                Oppy
              </Button>
              <Button
                variant="contained"
                size="large"
                onClick={() => {
                  // Open email client with pre-filled message
                  window.open(
                    "mailto:ag@oppy.pro?subject=Oppy Investment Inquiry&body=Hey Alex, I'd love to learn more about investing in Oppy. Can you please send me your SAFE? My name is",
                    "_blank"
                  );
                  console.log('Invest button clicked, opening email client');

                  // Dispatch analytics event for invest button click
                  dispatch(
                    trackAnalytics({
                      action_name: 'invest_button_clicked',
                      payload: {},
                    })
                  );
                  console.log('Analytics event dispatched: invest_button_clicked');
                }}
                sx={{
                  backgroundColor: theme.palette.secondary.main,
                  color: theme.palette.primary.contrastText,
                  fontWeight: 'bold',
                  mb: -2,
                  height: '100%',
                  '&:hover': {
                    backgroundColor: theme.palette.secondary.dark,
                  }
                }}
              >
                Invest/Inquire
              </Button>
              {/* Slide Counter */}
              <Typography
                variant="body2"
                sx={{
                  color: theme.palette.text.secondary,
                  ml: 2, // Margin left to space out from the button
                  mb:.5,
                  display:'flex',
                  alignItems:'end'
                }}
              >
                Slide: {currentSlide + 1}/{slides.length}
              </Typography>
            </Box>

            {/* Second Row: Navigation Controls */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                width: '100%',
                mb: -2,
              }}
            >

              {/* Left Arrow */}
              <Button
                size="large"
                onClick={() => {
                  handleBack();
                  console.log(`Navigated to previous slide: ${currentSlide}`);
                }}
                disabled={currentSlide === 0}
                sx={{
                  color: theme.palette.text.primary,
                  fontSize: { xs: '2rem', md: '3rem' },
                  minWidth: { xs: '48px', md: '72px' },
                  '&:hover': {
                    backgroundColor: theme.palette.action.hover,
                  },
                }}
              >
                <KeyboardArrowLeft fontSize="inherit" />
              </Button>
              
              {/* Clickable dots in the center */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexGrow: 1,
                  gap: 1,
                  mx: 2,
                }}
              >
                {slides.map((_, index) => (
                  <Box
                    key={index}
                    onClick={() => handleDotClick(index)}
                    sx={{
                      width: currentSlide === index ? 12 : 10,
                      height: currentSlide === index ? 12 : 10,
                      borderRadius: '50%',
                      backgroundColor: currentSlide === index
                        ? theme.palette.text.primary
                        : theme.palette.text.secondary,
                      opacity: currentSlide === index ? 1 : 0.4,
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        opacity: currentSlide === index ? 1 : 0.7,
                        transform: 'scale(1.2)',
                      },
                    }}
                  />
                ))}
              </Box>
              
              {/* Right Arrow */}
              <Button
                size="large"
                onClick={() => {
                  handleNext();
                  console.log(`Navigated to next slide: ${currentSlide + 2}`);
                }}
                disabled={currentSlide === slides.length - 1}
                sx={{
                  color: theme.palette.text.primary,
                  fontSize: { xs: '2rem', md: '3rem' },
                  minWidth: { xs: '48px', md: '72px' },
                  '&:hover': {
                    backgroundColor: theme.palette.action.hover,
                  },
                }}
              >
                <KeyboardArrowRight fontSize="inherit" />
              </Button>
            </Box>

          </Box>
        </Box>
      )}
    </>
  );
};

export default Deck;
